<template>
  <div class="ob-col">
    <div class="ob-head">
      <span class="ob-head__icon" style="background: rgba(240, 165, 0, 0.14); color: #f0a500"><span class="mdi mdi-office-building-outline"></span></span>
      <h2 class="ob-head__title">Agrega tu primer inquilino</h2>
      <p class="ob-head__sub">Podrás agregar más inquilinos después desde la sección de Contratos</p>
    </div>

    <div class="bulk">
      <div class="bulk__text">
        <b>¿Tienes varios inquilinos?</b>
        <span>Impórtalos todos juntos, con sus contratos, desde un Excel. Las naves deben estar registradas en el paso anterior.</span>
      </div>
      <button type="button" class="bulk__btn" :disabled="!hasSavedPlots" @click="showImport = true">
        <span class="mdi mdi-file-upload-outline"></span> {{ ob.contractsImported > 0 ? 'Importar más' : 'Importar contratos' }}
      </button>
    </div>

    <div v-if="ob.contractsImported > 0" class="ob-readonly">
      <span class="mdi mdi-check-circle" style="color: #10b981"></span>
      <span><b>{{ ob.contractsImported }}</b> {{ ob.contractsImported === 1 ? 'contrato importado' : 'contratos importados' }}. Puedes continuar; podrás agregar más después desde Contratos.</span>
    </div>

    <div v-if="ob.contractCreated" class="ob-readonly">
      <span class="mdi mdi-check-circle" style="color: #10b981"></span>
      <span><b>{{ ob.contractCreated.tenantName }}</b> ya quedó registrado en la nave {{ ob.contractCreated.plotName }}.</span>
    </div>

    <template v-else-if="ob.contractsImported === 0">
      <div class="ob-row2">
        <div class="ob-field">
          <label class="ob-label" for="t-name">Nombre de la empresa</label>
          <input id="t-name" v-model="ob.tenant.name" class="ob-input" placeholder="Ej: Manufacturas del Norte" maxlength="120" autocomplete="off" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="t-rfc">RFC de la empresa</label>
          <div class="ob-input-wrap">
            <input id="t-rfc" v-model="rfc" class="ob-input ob-input--mono" :class="{ 'ob-input--ok': rfcOk }" placeholder="ABC123456XYZ" maxlength="13" autocomplete="off" />
            <span v-if="rfcOk" class="ob-check mdi mdi-check-circle"></span>
          </div>
        </div>
        <div class="ob-field">
          <label class="ob-label" for="t-contact">Contacto de pagos</label>
          <input id="t-contact" v-model="ob.tenant.contactName" class="ob-input" placeholder="Nombre de quien recibe los cobros" maxlength="100" autocomplete="off" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="t-mail">Correo del contacto de pagos</label>
          <div class="ob-input-wrap">
            <input id="t-mail" v-model="ob.tenant.contactEmail" type="email" class="ob-input" :class="{ 'ob-input--ok': emailOk }" placeholder="pagos@empresa.com" autocomplete="off" />
            <span v-if="emailOk" class="ob-check mdi mdi-check-circle"></span>
          </div>
        </div>
      </div>

      <div class="ob-field">
        <label class="ob-label" for="t-plot">Nave que ocupa</label>
        <select id="t-plot" v-model="ob.tenant.plotId" class="ob-select" :disabled="!available.length">
          <option value="" disabled>{{ available.length ? 'Elige una nave' : 'Aún no hay naves registradas' }}</option>
          <option v-for="p in available" :key="p.id" :value="p.id">{{ p.name }}{{ p.areaM2 ? ` (${p.areaM2} m²)` : '' }}</option>
        </select>
        <p v-if="!available.length" class="ob-note">Registra al menos una nave en el paso anterior, o usa "Omitir este paso".</p>
      </div>
      <p class="ob-note">El inquilino se guarda junto con su contrato en el siguiente paso.</p>
    </template>

    <p v-if="ob.error" class="ob-error">{{ ob.error }}</p>

    <Teleport to="body">
      <ContractsImportPanel v-if="showImport" :park-id="ob.parkId" :z-index="3000" @close="showImport = false" @imported="onImported" />
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ob } from '@/services/onboardingStore'
import ContractsImportPanel from '@/components/Contratos/ContractsImportPanel.vue'
import { isValidRfc, isValidEmail } from './onboardingFormat'

const rfc = computed({
  get: () => ob.tenant.rfc,
  set: (v) => { ob.tenant.rfc = String(v).toUpperCase().replace(/[^A-ZÑ&0-9]/g, '') },
})
const rfcOk = computed(() => isValidRfc(ob.tenant.rfc))
const emailOk = computed(() => isValidEmail(ob.tenant.contactEmail))

const showImport = ref(false)
const hasSavedPlots = computed(() => ob.plots.some((p) => p.saved && p.id))
function onImported(count) {
  ob.contractsImported += Number(count) || 0
  ob.error = ''
}

// Solo naves ya guardadas (con id) y todavía libres.
const available = computed(() => ob.plots.filter((p) => p.saved && p.id && !p.occupied).map((p) => ({ id: p.id, name: p.name, areaM2: p.areaM2 })))
</script>

<style scoped>
.bulk { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 14px; margin-bottom: 18px; background: #eff6ff; border: 1px solid #dbeafe; border-radius: 8px; }
.bulk__text { display: flex; flex-direction: column; gap: 2px; font: 400 12px/1.5 Inter, sans-serif; color: #1e40af; }
.bulk__text b { font-weight: 600; color: #1e3a5f; font-size: 13px; }
.bulk__btn { flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 14px; border: 1px solid #3b82f6; border-radius: 8px; background: #fff; color: #2563eb; font: 500 12px Inter, sans-serif; cursor: pointer; }
.bulk__btn:hover:not(:disabled) { background: #eff6ff; }
.bulk__btn:disabled { opacity: 0.5; cursor: not-allowed; }
@media (max-width: 560px) { .bulk { flex-direction: column; align-items: stretch; } }
</style>